/**
 * PostHog Error Tracking: the triage envelope, the noise gate, and the
 * origin split — applied to every `$exception` event on its way out.
 *
 * PostHog's exception autocapture emits `$exception` with the error's type,
 * message and stack and nothing else. Panda runs `posthog.init` with
 * `autocapture: false`, so PostHog does NOT enrich custom events with
 * `$current_url` / `$browser` / `$os` the way it does pageviews — the same
 * omission that made the original publish-failure investigation stall (see
 * docs/failure-monitoring.md). An `$exception` therefore arrives with no route,
 * no project, no session and no environment unless we stamp them, which would
 * leave every issue impossible to scope: you could see that something threw,
 * but not to whom, where, or on which deploy.
 *
 * This module stamps the same triage envelope Panda's product failure events
 * already carry, so one exception and one `publish_failed` can be asked the
 * same question and joined on `project_id` + `session_id`.
 *
 * Pure except for the injected context provider, so the whole decision is
 * unit-testable without a browser or a PostHog instance.
 */

import {
  classifyPostHogException,
  isPostHogExceptionEvent,
  GENERATED_APP_PREVIEW_ORIGIN,
  type PostHogExceptionCaptureResult,
} from "./posthog-exception-event";

/**
 * Runtime facts the envelope needs, gathered by the caller (lib/analytics.ts)
 * because they require a live PostHog instance and a browser. Every field is
 * optional-by-value: an unknown fact is stamped as `null`, never omitted, so a
 * PostHog filter can tell "we did not know" apart from "the key is missing
 * because this event predates the change".
 */
export interface ExceptionContext {
  /** `production` / `preview` / `development` — matches every failure event. */
  environment: string;
  /** Low-cardinality route the exception was captured on. */
  route: string | null;
  /** The project workspace the user was in, joined to build/publish events. */
  project_id: string | null;
  /** PostHog session id + replay deep link — the "watch it happen" path. */
  session_id: string | null;
  session_replay_url: string | null;
  /** Signed-in user's email, when identify has already run. */
  email: string | null;
  /**
   * Whether this is Panda's own team / a QA throwaway account. Null before
   * identify. The canonical filter is the PERSON property of the same name;
   * this event-level copy is what makes anonymous internal sessions (a QA run
   * that throws before login) filterable too.
   */
  is_internal: boolean | null;
  /** Commit SHA + Vercel deployment id, so an issue names the deploy it came from. */
  release: string | null;
  deployment_id: string | null;
  /**
   * A deliberate failure fired from a drill surface to validate this pipeline,
   * not something a user hit. Every dashboard and alert excludes it; QA turns
   * the filter off to watch their own test land. See error-tracking-drill.ts.
   */
  is_qa_drill: boolean;
}

/** Context keys are stamped under these names on every `$exception`. */
export type ExceptionEnvelope = ExceptionContext & {
  error_origin: string;
  /**
   * The product phase, for exceptions reported from a known flow
   * (`captureClientException`). Autocaptured crashes have no phase — the
   * product did not plan for them — and stamp null.
   */
  failure_phase: string | null;
};

/**
 * Build the property envelope for one exception. Split out from
 * {@link applyErrorTracking} so the server path can stamp an identical shape.
 */
export function buildExceptionEnvelope(
  context: ExceptionContext,
  origin: string,
  failurePhase: string | null = null,
): ExceptionEnvelope {
  return { ...context, error_origin: origin, failure_phase: failurePhase };
}

/**
 * Envelope values only fill in what the call site did not already know.
 *
 * An exception reported from a KNOWN product flow (`captureClientException`)
 * arrives with a `failure_phase` and often a truer `project_id` than the URL
 * can give — a background publish that fails after the user navigated away is
 * still that project's failure. The ambient envelope is a fallback for
 * autocaptured crashes, so a caller-supplied non-null value always wins.
 * A caller-supplied `null` does not: that is "I looked and there is none",
 * which is exactly when the ambient value is worth trying.
 */
function withoutClobberingCallerValues(
  envelope: ExceptionEnvelope,
  existing: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!existing) return { ...envelope };
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(envelope)) {
    const supplied = existing[key];
    out[key] = supplied === undefined || supplied === null ? value : supplied;
  }
  return out;
}

/**
 * posthog-js `before_send` stage for Error Tracking. Non-`$exception` events
 * (pageviews, `$snapshot`, every product event) pass through untouched — this
 * composes after the session-replay console scrubber, not instead of it.
 *
 * For an `$exception` it does three things:
 *
 *  1. **Drops the five third-party noise classes** Sentry also drops, via the
 *     shared predicates. Without this, turning Error Tracking on would refill a
 *     fresh issue queue with the exact junk that was triaged out of the old one
 *     — Meta in-app-browser bridge beacons, browser translation DOM mutations,
 *     cross-origin frame reads from GTM/GA/the Meta pixel, Outlook Safe Links
 *     crawler rejections, and the browser's opaque cross-origin `Script error.`
 *     (which Sentry drops by default and PostHog does not).
 *
 *  2. **Stamps the triage envelope** so an issue can be scoped to a route,
 *     project, deploy, session and user.
 *
 *  3. **Splits generated-app crashes from Panda shell crashes.** A React crash
 *     inside the user's generated app running in the preview sandbox surfaces
 *     on `/project/:id` but is a bug in THAT app. It is kept (it is a real
 *     failure a real user hit) and tagged, never silently merged into a
 *     platform issue.
 *
 * Fail-safe by contract: any unexpected shape or thrown predicate returns the
 * event unmodified. Observability must never drop a real crash, and must never
 * break ingestion.
 */
export function applyErrorTracking<T extends PostHogExceptionCaptureResult | null>(
  cr: T,
  getContext: () => ExceptionContext,
): T | null {
  if (!cr || !isPostHogExceptionEvent(cr)) return cr;
  try {
    const verdict = classifyPostHogException(cr);
    if (verdict.drop) return null;

    const envelope = buildExceptionEnvelope(getContext(), verdict.origin);
    const properties: Record<string, unknown> = {
      ...cr.properties,
      ...withoutClobberingCallerValues(envelope, cr.properties),
      // `error_origin` is the one field the caller never gets to keep. The
      // verdict already honours an origin an upstream relay stamped (it is fed
      // back in through `tags`), and it is what the fingerprint below is keyed
      // on — so letting a stale caller value survive here would let an issue
      // claim one origin while being grouped under another.
      error_origin: verdict.origin,
    };

    if (verdict.origin === GENERATED_APP_PREVIEW_ORIGIN) {
      // Group generated-app crashes in their own bucket so a user's buggy app
      // can never read as a Panda platform outage. PostHog supports setting a
      // fingerprint at capture time; the array's tail keeps distinct app bugs
      // distinct WITHIN the bucket. If the override is ever ignored, the
      // property is inert and `error_origin` — which any issue-grouping rule or
      // saved filter can key on — still separates the two streams.
      properties.$exception_fingerprint = [
        GENERATED_APP_PREVIEW_ORIGIN,
        String(properties.$exception_type ?? "Error"),
        String(properties.$exception_message ?? ""),
      ];
    }

    return { ...cr, properties } as T;
  } catch {
    return cr;
  }
}
